
// Simple image cache to prevent re-loading textures every frame
const spriteCache: Record<string, HTMLImageElement> = {};

const getSprite = (src: string) => {
    if (!spriteCache[src]) {
        const img = new Image();
        img.src = src;
        spriteCache[src] = img;
    }
    return spriteCache[src];
};

export type SpriteEffectData = {
    kind: 'sprite';
    t: number;            // Current time in ms
    x: number;            // Center X
    y: number;            // Center Y

    // Sprite configuration
    spriteUrl: string;    // Path to image
    frameWidth: number;   // Width of a single frame
    frameHeight: number;  // Height of a single frame
    totalFrames: number;  // Total number of frames in the strip
    fps: number;          // Frames per second
    loop?: boolean;       // Loop animation?

    // Optional
    scale?: number;       // Resize factor (default 1)
    rotation?: number;    // Rotation in radians
    opacity?: number;     // Transparency (0-1)
};

export const createSpriteEffect = (
    x: number,
    y: number,
    config: Omit<SpriteEffectData, 'kind' | 't' | 'x' | 'y'>
): SpriteEffectData => {
    return {
        kind: 'sprite',
        t: 0,
        x,
        y,
        ...config,
        loop: config.loop ?? false,
        scale: config.scale ?? 1,
        rotation: config.rotation ?? 0,
        opacity: config.opacity ?? 1,
    };
};

export const renderSpriteEffect = (
    effect: SpriteEffectData,
    ctx: CanvasRenderingContext2D
): boolean => {
    const img = getSprite(effect.spriteUrl);

    // If image not loaded, keep effect alive but draw nothing
    if (!img.complete) return true;

    const msPerFrame = 1000 / effect.fps;
    const duration = msPerFrame * effect.totalFrames;

    // Check storage/life
    if (!effect.loop && effect.t > duration) {
        return false; // Kill effect
    }

    // Calculate current frame
    // If looping: modulo total time. If not: clamp to last frame (or kill above)
    const currentFrameIdx = Math.floor(effect.t / msPerFrame) % effect.totalFrames;

    const sx = currentFrameIdx * effect.frameWidth;
    const sy = 0; // Assuming single-row strip
    const sw = effect.frameWidth;
    const sh = effect.frameHeight;

    ctx.save();
    ctx.translate(effect.x, effect.y);
    if (effect.rotation) ctx.rotate(effect.rotation);
    if (effect.scale !== 1) ctx.scale(effect.scale, effect.scale);
    if (effect.opacity !== 1) ctx.globalAlpha = effect.opacity;

    // Draw centered
    ctx.drawImage(
        img,
        sx, sy, sw, sh,
        -sw / 2, -sh / 2, sw, sh
    );

    ctx.restore();

    return true;
};
